import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";

const ironmanFranceNice: ChauffeurPage = {
  slug: "ironman-france-nice",
  title: "Chauffeur privé Ironman France Nice – VTC & transferts",
  subtitle:
    "Chauffeur privé pendant l'Ironman France à Nice : départ tôt sur la baie des Anges, accès malgré les routes fermées, transferts aéroport et suivi des supporters.",
  keywords: [
    "chauffeur ironman nice",
    "vtc ironman france nice",
    "chauffeur privé triathlon nice",
  ],
  hero: {
    title: "Chauffeur privé pendant l'Ironman France à Nice",
    subtitle:
      "Natation baie des Anges, vélo dans l'arrière-pays, marathon sur la Promenade : un chauffeur dédié quand les routes se ferment et que les départs sont matinaux.",
    keywords: [
      "chauffeur Ironman France Nice",
      "VTC triathlon Nice",
      "transfert aéroport Nice Ironman",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé pendant l'Ironman France à Nice",
    badges: ["Départs matinaux", "Accueil aéroport", "Suivi des supporters"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur pendant l'Ironman France",
    items: [
      {
        title: "Routes fermées maîtrisées",
        description:
          "La Promenade des Anglais et des routes de l'arrière-pays ferment le jour de course. Le chauffeur connaît les accès vers le départ et les points de spectacle.",
      },
      {
        title: "Départs très matinaux",
        description:
          "La natation part tôt sur la baie des Anges : le chauffeur vient vous chercher à l'heure, à froid, sans stress de stationnement.",
      },
      {
        title: "Aéroport à quelques minutes",
        description:
          "Nice Côte d'Azur est à moins de 7 km. Suivi de vol, accueil pancarte et transfert direct avec vos bagages et votre matériel.",
      },
      {
        title: "5,0/5 sur 38 avis Google",
        description:
          "Ponctualité, discrétion et véhicules soignés : la régularité qui vaut à KS Rent Services une note de 5,0/5 sur 38 avis Google.",
      },
    ],
  },
  services: {
    heading: "Services chauffeur pendant l'Ironman France à Nice",
    services: [
      {
        title: "Transfert matinal vers le départ",
        text: "Dépose près de la baie des Anges pour l'entrée dans l'eau, tôt le matin, à l'heure convenue.",
      },
      {
        title: "Accueil aéroport de Nice",
        text: "Transfert aéroport ↔ hôtel avec suivi de vol, à quelques minutes de la Promenade des Anglais.",
      },
      {
        title: "Suivi des supporters",
        text: "Nous amenons familles et supporters aux points clés du parcours vélo et du marathon, selon les routes ouvertes.",
      },
      {
        title: "Mise à disposition journée",
        text: "Chauffeur dédié pour une longue journée de course, du départ à l'arrivée et à la récupération.",
      },
      {
        title: "Navettes équipes & groupes",
        text: "Coordination multi-véhicules pour athlètes, accompagnants et bagages.",
      },
    ],
  },
  locations: {
    heading: "Points clés pendant l'Ironman France",
    items: [
      {
        name: "Baie des Anges / Promenade des Anglais",
        description:
          "Départ de la natation et arrivée du marathon en front de mer.",
      },
      {
        name: "Arrière-pays niçois",
        description: "Routes et cols du parcours vélo, points de spectacle.",
      },
      {
        name: "Aéroport de Nice Côte d'Azur",
        description: "À moins de 7 km, terminaux 1 et 2.",
      },
      {
        name: "Vieux Nice & hôtels",
        description:
          "Hébergements du centre et du front de mer, proches du départ.",
      },
      {
        name: "Zone d'arrivée & récupération",
        description: "Reprise après l'effort, transfert direct vers l'hôtel.",
      },
    ],
  },
  generic: {
    heading: "L'Ironman France à Nice vu d'un chauffeur",
    subheading:
      "Chaque année, Nice accueille un triathlon longue distance : natation dans la baie des Anges, vélo dans l'arrière-pays et marathon sur la Promenade des Anglais.",
    paragraphs: [
      "Chaque année, l'Ironman France transforme Nice le temps d'une longue journée de course. La natation s'élance tôt le matin dans la baie des Anges, le parcours vélo grimpe dans l'arrière-pays niçois, et le marathon se court sur la Promenade des Anglais. Selon l'édition, l'épreuve se tient au début de l'été ou à l'automne.",
      "Le jour de course, la Promenade et des routes de l'arrière-pays ferment. Un chauffeur qui connaît les accès vous amène au départ à l'heure et conduit vos supporters aux meilleurs points de spectacle, sans se perdre dans les déviations.",
      "L'aéroport de Nice Côte d'Azur est à moins de sept kilomètres. Avec le suivi de vol et l'accueil pancarte, votre transfert se fait sans attente, bagages et matériel compris, à l'aller comme au retour après la course.",
    ],
    bullets: [
      "Dépose près du départ",
      "Suivi du parcours pour les supporters",
      "Aéroport à moins de 7 km",
    ],
  },
  process: {
    heading: "Réserver un chauffeur pour l'Ironman France à Nice",
    steps: [
      "Indiquez votre hôtel, l'heure de départ et le nombre de supporters à transporter.",
      "Nous calons les itinéraires selon les routes fermées de la Promenade et de l'arrière-pays.",
      "Le chauffeur se présente à l'heure pour le départ matinal, avec suivi de vol pour vos arrivées.",
      "Transfert direct ou mise à disposition pour couvrir départ, parcours et récupération.",
    ],
  },
  faq: [
    {
      q: "Comment réserver un chauffeur privé pendant l'Ironman France à Nice ?",
      a: "Réservez à l'avance : le week-end de course, Nice est très demandée et les départs sont matinaux. Précisez votre hôtel, l'heure de départ et le nombre de supporters pour que nous bloquions un chauffeur dédié.",
    },
    {
      q: "Le chauffeur peut-il m'amener au départ tôt le matin ?",
      a: "Oui. La natation part très tôt sur la baie des Anges : le chauffeur vient vous chercher à l'heure convenue et vous dépose au plus près de l'entrée dans l'eau, sans souci de stationnement.",
    },
    {
      q: "Comment gérez-vous les routes fermées le jour de course ?",
      a: "La Promenade des Anglais et des routes de l'arrière-pays sont fermées pendant l'épreuve. Le chauffeur connaît les déviations et les accès autorisés pour rejoindre le départ et les points de spectacle sans se perdre.",
    },
    {
      q: "Pouvez-vous conduire mes supporters le long du parcours ?",
      a: "Oui. Nous amenons familles et supporters aux points clés du parcours vélo et du marathon, selon les routes ouvertes, pour vous encourager à plusieurs endroits de la journée.",
    },
    {
      q: "Gérez-vous le transfert depuis l'aéroport avec le matériel ?",
      a: "Oui, avec suivi de vol et accueil pancarte. L'aéroport de Nice Côte d'Azur est à moins de 7 km : nous prévoyons un véhicule adapté à vos bagages et à votre matériel, à l'aller comme au retour.",
    },
    {
      q: "Assurez-vous la récupération après la course ?",
      a: "Oui. Après une longue journée d'effort, le chauffeur vous reprend à l'arrivée et vous ramène directement à l'hôtel, sans attente et sans marche supplémentaire.",
    },
    {
      q: "Quand a lieu l'Ironman France à Nice ?",
      a: "L'épreuve se tient chaque année à Nice ; selon l'édition, elle a lieu au début de l'été ou à l'automne. Communiquez-nous vos dates exactes et nous organisons vos transferts autour de la course.",
    },
  ],
  relatedLinks: [
    {
      label: "Réserver un chauffeur à Nice",
      href: "/chauffeur/nice",
    },
    {
      label: "Accueil à l’aéroport de Nice",
      href: "/chauffeur/aeroport",
    },
    {
      label: "Chauffeur Côte d’Azur, tarifs et trajets",
      href: "/chauffeur/cote-d-azur",
    },
    {
      label: "Voitures à louer à Nice",
      href: "/location/location-voiture-nice",
    },
  ],
  cta: {
    title: "Votre chauffeur pour l'Ironman France à Nice",
    text: "Départ matinal, accès malgré les routes fermées et suivi des supporters : réservez un chauffeur dédié pour votre week-end de course.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver un chauffeur",
  },
  seo: {
    title: "Chauffeur privé Ironman France Nice | KS Rent",
    description:
      "Chauffeur privé pendant l'Ironman France à Nice : départ matinal baie des Anges, accès malgré les routes fermées, transfert aéroport et suivi supporters.",
    canonical: "/chauffeur/ironman-france-nice",
  },
};

export default ironmanFranceNice;
